import 'regenerator-runtime/runtime';
import '@miroculus/nucleo/static/fonts/Montserrat.css';
import 'react-toastify/dist/ReactToastify.min.css';
import 'app.scss';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ToastContainer, toast, Flip } from 'react-toastify';
import { Link, BrowserRouter } from 'react-router-dom';
import apiClient from '@miroculus/api-client';
import { Context as MiroculusUIContext } from '@miroculus/miroculus-ui';
import { NucleoProvider, GlobalStyles } from '@miroculus/nucleo';
import socketClient from 'socketClient';
import { initMessage } from 'remoteMessage';
import { NavigationInitializer, navigationService } from 'navigation';
import configureStore from './store/configureStore';
import Routes from './routes';
import { LOGOUT_URL } from 'cons/routes';
import { initNotifications } from 'notifications';
import { updateCameraId } from 'reduxModules/settings';
import { addAPIHealthData } from 'reduxModules/log';
import InjectCSSVariables from './utils/InjectCSSVariables';
import { getEncryptionKey } from './utils/storage';
import {
  ConfirmContainer,
  ConfirmProvider,
  BreadcrumbProvider,
} from 'components';

const API_URL = process.env.API_URL;
const APP_VERSION = process.env.APP_VERSION || 'dev';

// Log app version on startup
console.info(`🧬 Miroculus App v${APP_VERSION}`);

apiClient.init({
  apiUrl: API_URL,
  encryptionKey: getEncryptionKey(),
  onLogout: () => {
    navigationService.push(LOGOUT_URL);
  },
});

// set up the initial redux state with the user, if any
const user = apiClient.getUser();
const auth = user ? { user, logged: true, loading: true } : undefined;

const store = configureStore({ auth });

apiClient.head('/health').then(({ res }) => {
  const data = {};
  res.headers.forEach((value, header) => {
    if (header.startsWith('x-miroculus-')) {
      const key = header.replace('x-miroculus-', '').replace('-', ' ');
      data[key] = value;
    }
  });
  store.dispatch(addAPIHealthData(data));
});

initNotifications();
initMessage(store);
socketClient.init();

const storedCameraId = apiClient.storage.get('cameraId');
if (storedCameraId) {
  store.dispatch(updateCameraId(storedCameraId));
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <Provider store={store}>
    <InjectCSSVariables />
    <GlobalStyles />
    <MiroculusUIContext.Provider value={{ LinkComponent: Link }}>
      <NucleoProvider value={{ LinkComponent: Link }}>
        <ConfirmProvider>
          <BreadcrumbProvider>
            <BrowserRouter>
              <NavigationInitializer />
              <Routes />
              <ToastContainer transition={Flip} />
              <ConfirmContainer />
            </BrowserRouter>
          </BreadcrumbProvider>
        </ConfirmProvider>
      </NucleoProvider>
    </MiroculusUIContext.Provider>
  </Provider>
);

if (window.Cypress) {
  window.store = store;
  window.apiClient = apiClient;
}

if (process.env.NODE_ENV === 'development') {
  // To debug toast notifications!
  window.toast = toast;
}
